import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Play, Pause, Radio, ArrowLeft, Loader2, Copy, Hand } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Header } from "@/components/Header";
import { fmtTime } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ListenTogether() {
  const { code } = useParams();
  if (!code) return <CreateSession />;
  return <Session code={code.toUpperCase()} />;
}

function CreateSession() {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [songs, setSongs] = useState([]);
  const [songId, setSongId] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { axios.get(`${API}/songs`).then((r) => { setSongs(r.data); setSongId(r.data[0]?.id || ""); }).catch(() => {}); }, []);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/listen`, { song_id: songId });
      sessionStorage.setItem(`sk_listen_host_${data.code}`, "1");
      nav(`/rooms/sindu-kamare/listen/${data.code}`);
    } catch (e) { toast.error("Could not start the session."); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <Header />
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-12 text-center">
        <span className="grid place-items-center w-16 h-16 rounded-2xl sk-glow mx-auto mb-4" style={{ background: "var(--player-bg)", color: "var(--player-accent)" }}><Radio size={30} /></span>
        <h1 className={`${lang === "si" ? "font-si" : "font-en"} text-3xl sm:text-4xl font-bold`} style={{ color: "var(--text)" }}>{t("lt_title")}</h1>
        <p className="font-ui text-base mt-3 leading-relaxed" style={{ color: "var(--text-2)" }}>{t("lt_sub")}</p>
        <div className="rounded-3xl p-6 mt-6 text-left" style={{ background: "var(--card)", border: "2px solid var(--border)" }}>
          <label className="block"><span className="font-ui text-sm font-bold" style={{ color: "var(--text-2)" }}>{t("lt_pick")}</span>
            <select data-testid="listen-song-select" value={songId} onChange={(e) => setSongId(e.target.value)} className="mt-1 w-full h-12 px-4 rounded-xl font-ui outline-none" style={{ background: "var(--bg)", border: "2px solid var(--border)", color: "var(--text)" }}>
              {songs.map((s) => <option key={s.id} value={s.id}>{`${lang === "si" ? s.title_si : s.title_en} — ${s.artist}`}</option>)}
            </select></label>
          <button onClick={start} disabled={busy || !songId} data-testid="listen-start-button" className="mt-4 w-full h-14 rounded-full font-ui text-lg font-bold flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "var(--accent)", color: "#fff" }}>
            {busy ? <Loader2 className="animate-spin" size={20} /> : <Radio size={20} />} {t("lt_create")}
          </button>
        </div>
        <Link to="/rooms/sindu-kamare" className="flex items-center justify-center gap-2 font-ui text-sm font-semibold mt-8" style={{ color: "var(--accent)" }}><ArrowLeft size={16} /> {t("back_room")}</Link>
      </div>
    </div>
  );
}

function Session({ code }) {
  const { t, lang } = useLang();
  const isHost = sessionStorage.getItem(`sk_listen_host_${code}`) === "1";
  const audioRef = useRef(null);
  const [state, setState] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [needTap, setNeedTap] = useState(false);
  const loadedSongRef = useRef(null);

  useEffect(() => { audioRef.current = new Audio(); audioRef.current.preload = "auto"; return () => { try { audioRef.current?.pause(); } catch (e) {} }; }, []);

  const pushState = useCallback((over = {}) => {
    if (!isHost || !state?.song) return;
    const a = audioRef.current;
    axios.put(`${API}/listen/${code}`, {
      song_id: state.song.id,
      position_sec: over.position_sec ?? (a ? a.currentTime : 0),
      is_playing: over.is_playing ?? (a ? !a.paused : false),
    }).catch(() => {});
  }, [isHost, state, code]);

  // initial + polling fetch
  useEffect(() => {
    let live = true;
    const fetchState = () => axios.get(`${API}/listen/${code}`).then((r) => { if (live) setState(r.data); }).catch(() => {});
    fetchState();
    const iv = setInterval(() => { if (!isHost) fetchState(); }, 2500);
    return () => { live = false; clearInterval(iv); };
  }, [code, isHost]);

  // host pushes periodically
  useEffect(() => {
    if (!isHost) return;
    const iv = setInterval(() => pushState(), 2500);
    return () => clearInterval(iv);
  }, [isHost, pushState]);

  // apply remote state to the guest audio
  useEffect(() => {
    if (!state?.song) return;
    const a = audioRef.current;
    if (!a) return;
    if (loadedSongRef.current !== state.song.id) {
      loadedSongRef.current = state.song.id;
      a.src = state.song.stream_url;
      a.load();
      setDuration(state.song.duration_sec || 0);
    }
    if (isHost) return; // host controls locally
    const drift = state.is_playing ? (Date.now() - new Date(state.updated_at).getTime()) / 1000 : 0;
    const target = (state.position_sec || 0) + drift;
    if (Math.abs((a.currentTime || 0) - target) > 2.5) { try { a.currentTime = target; } catch (e) {} }
    if (state.is_playing && a.paused) { a.play().then(() => setNeedTap(false)).catch(() => setNeedTap(true)); }
    if (!state.is_playing && !a.paused) a.pause();
  }, [state, isHost]);

  // audio listeners
  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    const onT = () => setProgress(a.currentTime);
    const onMeta = () => setDuration(a.duration || 0);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    a.addEventListener("timeupdate", onT); a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("play", onPlay); a.addEventListener("pause", onPause);
    return () => { a.removeEventListener("timeupdate", onT); a.removeEventListener("loadedmetadata", onMeta); a.removeEventListener("play", onPlay); a.removeEventListener("pause", onPause); };
  }, [state?.song?.id]);

  const hostToggle = () => { const a = audioRef.current; if (!a) return; if (a.paused) { a.play().catch(() => setNeedTap(true)); pushState({ is_playing: true }); } else { a.pause(); pushState({ is_playing: false }); } };
  const hostSeek = (v) => { const a = audioRef.current; if (a) { a.currentTime = v; pushState({ position_sec: v }); } };
  const guestJoin = () => { const a = audioRef.current; if (a) a.play().then(() => setNeedTap(false)).catch(() => {}); };

  const song = state?.song;
  const shareUrl = `${window.location.origin}/rooms/sindu-kamare/listen/${code}`;

  return (
    <div className="min-h-screen" style={{ background: "var(--player-bg)" }}>
      <div className="max-w-xl mx-auto px-5 py-8 text-center">
        <div className="flex items-center justify-between mb-6">
          <Link to="/rooms/sindu-kamare" className="grid place-items-center w-11 h-11 rounded-full" style={{ background: "rgba(255,255,255,0.1)", color: "#FFFDF7" }}><ArrowLeft size={20} /></Link>
          <span className="font-ui text-sm font-bold px-3 py-1.5 rounded-full flex items-center gap-2" style={{ background: "rgba(245,158,11,0.18)", color: "var(--player-accent)" }}><Radio size={14} /> {code}</span>
          <span className="w-11" />
        </div>

        {!song ? (
          <div className="py-24" style={{ color: "rgba(255,255,255,0.6)" }}><Loader2 className="animate-spin inline" /> <span className="font-ui ml-2">{t("lt_waiting")}</span></div>
        ) : (
          <>
            <p className="font-ui text-sm font-bold" style={{ color: "var(--player-accent)" }}>{isHost ? t("lt_host") : t("lt_guest")}</p>
            <img src={song.cover_url} alt="" className="w-60 h-60 rounded-3xl object-cover mx-auto mt-4 shadow-2xl" />
            <h1 className={`${lang === "si" ? "font-si" : "font-en"} text-2xl sm:text-3xl font-bold mt-5`} style={{ color: "#FFFDF7" }}>{lang === "si" ? song.title_si : song.title_en}</h1>
            <p className="font-ui text-base" style={{ color: "rgba(255,255,255,0.65)" }}>{song.artist}</p>

            <div className="mt-6">
              <input type="range" className="sk-range" min={0} max={duration || 0} step={0.5} value={Math.min(progress, duration || 0)} disabled={!isHost} data-testid="listen-scrub" onChange={(e) => hostSeek(parseFloat(e.target.value))} style={{ background: `linear-gradient(to right, var(--player-accent) ${duration ? (progress / duration) * 100 : 0}%, rgba(255,255,255,0.18) ${duration ? (progress / duration) * 100 : 0}%)` }} />
              <div className="flex justify-between font-ui text-sm font-semibold mt-1" style={{ color: "#FFFDF7" }}><span>{fmtTime(progress)}</span><span>{fmtTime(duration)}</span></div>
            </div>

            {isHost ? (
              <button onClick={hostToggle} data-testid="listen-host-toggle" className="mt-6 grid place-items-center w-24 h-24 rounded-full mx-auto sk-glow" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}>
                {isPlaying ? <Pause size={44} fill="currentColor" /> : <Play size={44} fill="currentColor" style={{ marginLeft: 4 }} />}
              </button>
            ) : needTap ? (
              <button onClick={guestJoin} data-testid="listen-join-button" className="mt-6 inline-flex items-center gap-2 h-14 px-8 rounded-full font-ui text-lg font-bold" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}>
                <Hand size={20} /> {t("lt_tap_join")}
              </button>
            ) : (
              <p className="mt-6 font-ui" style={{ color: "rgba(255,255,255,0.7)" }}>{isPlaying ? "♪♪♪" : "…"}</p>
            )}

            {isHost && (
              <div className="mt-8 rounded-2xl p-4 text-left" style={{ background: "rgba(255,255,255,0.06)" }}>
                <p className="font-ui text-sm font-bold mb-2" style={{ color: "var(--player-accent)" }}>{t("lt_share")}</p>
                <div className="flex gap-2">
                  <input readOnly value={shareUrl} data-testid="listen-share-url" className="flex-1 h-11 px-3 rounded-lg font-ui text-sm" style={{ background: "var(--player-bg)", color: "#FFFDF7", border: "1px solid rgba(255,255,255,0.15)" }} />
                  <button onClick={() => { navigator.clipboard?.writeText(shareUrl); toast.success(t("dd_copied")); }} data-testid="listen-copy" className="grid place-items-center w-11 h-11 rounded-lg shrink-0" style={{ background: "var(--player-accent)", color: "var(--player-bg)" }}><Copy size={18} /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
